/**
 * Turns a conversational instruction about a description into a draft sentence.
 *
 * "…make sure they ask for Bobby at the gate" -> "Ask for Bobby at the gate."
 *
 * This is a rule-based stand-in for a model-backed writer, and it is genuinely
 * weak at prose: it can only re-point and tidy the user's own words. That is why
 * the draft is always surfaced in an editable field before it is staged — the
 * user corrects the wording rather than accepting whatever the rules produced.
 */

/** Phrases that introduce the substance of the request. */
const INTENT_LEADERS = [
  'make sure that',
  'make sure',
  'makes sure',
  'ensure that',
  'ensure',
  'let them know that',
  'let them know',
  'let people know',
  'so they know that',
  'so they know',
  'remind them to',
  'remind them that',
  'mention that',
  'mention',
  'say that',
  'say',
  'tell them to',
  'tell them that',
  'tell them',
  'note that',
  'include that',
  'include',
];

/** Leading subjects that should drop when the clause becomes an instruction. */
const SUBJECT_PREFIXES = [
  'that they should',
  'that they need to',
  'that they',
  'they should',
  'they need to',
  'they must',
  'they',
  'drivers should',
  'drivers need to',
  'drivers',
  'people should',
  'people',
  'customers should',
  'customers',
  'buyers should',
  'buyers',
  'everyone should',
  'everyone',
  'you should',
  'to',
  'that',
];

const stripLeading = (text: string, prefixes: string[]): string => {
  const lower = text.toLowerCase();
  for (const prefix of prefixes) {
    if (lower.startsWith(`${prefix} `)) {
      return text.slice(prefix.length + 1).trim();
    }
  }
  return text;
};

/**
 * Extracts the instruction clause from a message, or null when the message does
 * not read as a semantic description request.
 */
export const extractInstructionClause = (message: string): string | null => {
  const lower = message.toLowerCase();

  let bestIndex = -1;
  let bestLeader = '';
  for (const leader of INTENT_LEADERS) {
    const index = lower.indexOf(` ${leader} `);
    // Prefer the earliest leader; on a tie prefer the longest match.
    if (index !== -1 && (bestIndex === -1 || index < bestIndex || (index === bestIndex && leader.length > bestLeader.length))) {
      bestIndex = index;
      bestLeader = leader;
    }
  }

  if (bestIndex === -1) return null;

  const clause = message.slice(bestIndex + bestLeader.length + 2).trim();
  return clause || null;
};

/**
 * Normalizes a clause into a standalone imperative sentence: drops the subject,
 * capitalizes, and terminates it.
 */
export const draftSentence = (clause: string): string => {
  let text = clause.trim().replace(/[\s.]+$/, '');
  text = stripLeading(text, SUBJECT_PREFIXES);
  if (!text) return '';

  const capitalized = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`;
};

/**
 * Convenience wrapper: message in, draft sentence out (or null when the message
 * is not a semantic description instruction).
 */
export const draftFromMessage = (message: string): string | null => {
  const clause = extractInstructionClause(message);
  if (!clause) return null;
  const sentence = draftSentence(clause);
  return sentence || null;
};
